import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, InputAdornment, MenuItem, Skeleton, Step, StepLabel, Stepper,
  Switch, TextField, Typography,
} from '@mui/material';
import { MdArrowBack, MdClose, MdInventory2 } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { createCombo, getComboById, updateCombo } from '../../Webservice/offerAPI';
import { getData } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';
import { FONT, authToken, imageUrl, yellowBtn } from './bogoShared';
import OfferImages, { appendImages, savedImages } from './OfferImages';
import { MixNotice, PriceBreakdown, StockChip, isSeller, stockInfo } from './comboPricing';

const STEPS = ['Basics', 'Products', 'Pricing', 'Review'];
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const Card = ({ title, hint, accent, children }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.5 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{title}</Typography>
    {hint && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1.5 }}>{hint}</Typography>}
    {children}
  </Box>
);

function CreateComboOffer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [step, setStep] = useState(0);
  const [loadingCombo, setLoadingCombo] = useState(isEdit);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [filterSub, setFilterSub] = useState('');

  const [form, setForm] = useState({
    title: '', comboCode: '', description: '', visibility: ['b2b', 'b2c'], startDate: '', endDate: '',
    lines: [], // { product, quantity }
    b2c_sellingPrice: '', b2b_sellingPrice: '', isActive: true,
    images: [],
  });
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };

  // totals follow the lines
  const mrp = useMemo(() => form.lines.reduce((a, l) => a + (Number(l.product.b2c_mrp) || 0) * l.quantity, 0), [form.lines]);
  const separately = useMemo(() => form.lines.reduce((a, l) => a + (Number(l.product.b2c_sellingPrice) || Number(l.product.b2c_mrp) || 0) * l.quantity, 0), [form.lines]);
  const combosPossible = useMemo(() => {
    if (!form.lines.length) return 0;
    const m = Math.min(...form.lines.map((l) => stockInfo(l.product, l.quantity).makes));
    return m === Infinity ? 10 : m; // only seller products: orders are capped at 10 per customer
  }, [form.lines]);
  const b2cPrice = Number(form.b2c_sellingPrice) || 0;
  const savePct = mrp > 0 && b2cPrice > 0 && b2cPrice < mrp ? Math.round(((mrp - b2cPrice) / mrp) * 100) : 0;

  useEffect(() => {
    if (!id) return;
    getComboById(id)
      .then((res) => {
        const c = res?.data || res?.combo;
        if (!c) throw new Error('missing');
        setForm({
          title: c.title || '', comboCode: c.comboCode || '', description: c.description || '', visibility: c.visibility?.length ? c.visibility : ['b2b', 'b2c'],
          startDate: c.startDate ? String(c.startDate).split('T')[0] : '', endDate: c.endDate ? String(c.endDate).split('T')[0] : '',
          lines: (c.products || []).filter((p) => p.product_id && typeof p.product_id === 'object').map((p) => ({ product: p.product_id, quantity: p.quantity || 1 })),
          b2c_sellingPrice: c.b2c_sellingPrice ?? '', b2b_sellingPrice: c.b2b_sellingPrice ?? '', isActive: c.isActive !== false,
          images: savedImages(c.product_images || []),
        });
      })
      .catch(() => setLoadError('Could not load this combo.'))
      .finally(() => setLoadingCombo(false));
  }, [id]);

  useEffect(() => {
    getData('categories/list?limit=1000', null, authToken(), false)
      .then((res) => setCategories(Array.isArray(res?.categories) ? res.categories : []))
      .catch(() => setCategories([]));
  }, []);
  useEffect(() => {
    if (!filterCat) { setSubcategories([]); return; }
    getData(`subcategories/list?limit=1000&category_id=${filterCat}`, null, authToken(), false)
      .then((res) => setSubcategories(Array.isArray(res?.subcategories) ? res.subcategories : []))
      .catch(() => setSubcategories([]));
  }, [filterCat]);
  useEffect(() => {
    setProductsLoading(true);
    const q = new URLSearchParams({ limit: '1000' });
    if (filterCat) q.append('categoryId', filterCat);
    if (filterSub) q.append('subcategoryId', filterSub);
    getData(`${PRODUCT_ROUTES.GET_PRODUCT}?${q.toString()}`, null, authToken(), false)
      .then((res) => setAllProducts(Array.isArray(res?.products) ? res.products : []))
      .catch(() => setAllProducts([]))
      .finally(() => setProductsLoading(false));
  }, [filterCat, filterSub]);

  const setProducts = (selected) => setForm((f) => ({
    ...f,
    lines: selected.map((p) => f.lines.find((l) => l.product._id === p._id) || { product: p, quantity: 1 }),
  }));
  const setQty = (pid, value) => {
    const q = parseInt(value, 10);
    setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.product._id === pid ? { ...l, quantity: Number.isInteger(q) && q > 0 ? q : 1 } : l)) }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.title.trim()) e.title = 'Give the combo a name';
      if (!isEdit) {
        if (!form.comboCode.trim()) e.comboCode = 'A code is required';
        else if (!/^[A-Za-z0-9_-]{2,30}$/.test(form.comboCode.trim())) e.comboCode = 'Letters, numbers, - and _ only (2 to 30)';
      }
      if (form.visibility.length === 0) e.visibility = 'Choose at least one customer type';
      if (form.startDate && form.endDate && form.endDate < form.startDate) e.endDate = 'Must be on or after the start date';
    }
    if (s === 1 && form.lines.length === 0) e.products = 'Add at least one product';
    if (s === 2) {
      if (!(Number(form.b2c_sellingPrice) > 0)) e.b2c_sellingPrice = 'Enter a price above zero';
      if (form.b2b_sellingPrice !== '' && !(Number(form.b2b_sellingPrice) >= 0)) e.b2b_sellingPrice = 'Enter 0 or more';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const next = () => { if (validateStep(step)) setStep(step + 1); };

  const submit = async () => {
    for (let s = 0; s < 3; s++) if (!validateStep(s)) { setStep(s); return; }
    setSaving(true);
    setSubmitError('');
    try {
      const body = new FormData();
      body.append('title', form.title.trim());
      body.append('description', form.description.trim());
      body.append('comboCode', form.comboCode.trim().toUpperCase());
      body.append('visibility', JSON.stringify(form.visibility));
      body.append('products', JSON.stringify(form.lines.map((l) => ({ product_id: l.product._id, quantity: l.quantity }))));
      body.append('b2c_mrp', mrp);
      body.append('b2b_mrp', mrp);
      body.append('b2c_sellingPrice', form.b2c_sellingPrice);
      body.append('b2b_sellingPrice', form.b2b_sellingPrice === '' ? '' : form.b2b_sellingPrice);
      body.append('isActive', form.isActive);
      body.append('startDate', form.startDate);
      body.append('endDate', form.endDate);
      body.append('entity', 'Combo Offer');
      appendImages(body, form.images);

      const res = isEdit ? await updateCombo(id, body) : await createCombo(body);
      if (!res?.success) throw new Error(res?.message || 'Could not save the combo');
      navigate(isEdit ? `/offers/combo/view/${id}` : '/offers/combo');
    } catch (err) {
      setSubmitError(err?.message || 'Could not save the combo.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingCombo) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={420} sx={{ borderRadius: '14px' }} /></Box>;
  if (loadError) return <Box sx={{ p: 3, maxWidth: 520, mx: 'auto' }}><Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert><Button onClick={() => navigate('/offers/combo')} sx={yellowBtn}>Back to combos</Button></Box>;

  const visToggle = (k) => set('visibility', form.visibility.includes(k) ? form.visibility.filter((x) => x !== k) : [...form.visibility, k]);
  const first = form.images[0];
  const shownImage = first ? (first.file ? first.preview : first.saved) : '';

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <IconButton onClick={() => navigate(isEdit ? `/offers/combo/view/${id}` : '/offers/combo')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>{isEdit ? 'Edit combo' : 'Create combo'}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>{form.title || 'A bundle of products at one price'}</Typography>
        </Box>
      </Box>

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', p: { xs: 2, md: 3 }, mb: 2 }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((l, i) => <Step key={l} completed={i < step}><StepLabel sx={{ cursor: i < step ? 'pointer' : 'default' }} onClick={() => i < step && setStep(i)}>{l}</StepLabel></Step>)}
        </Stepper>
      </Box>

      {step === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card title="Combo details" hint="Name and code shown to your team" accent="#6366f1">
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' } }}>
              <TextField size="small" label="Combo title *" value={form.title} onChange={(e) => set('title', e.target.value)} error={!!errors.title} helperText={errors.title} />
              <TextField size="small" label="Combo code *" value={form.comboCode} onChange={(e) => set('comboCode', e.target.value.toUpperCase())} disabled={isEdit} error={!!errors.comboCode} helperText={errors.comboCode || (isEdit ? 'Cannot be changed' : 'Unique, like SUMMER-KIT')} inputProps={{ maxLength: 30 }} />
              <TextField size="small" label="Description" multiline rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} sx={{ gridColumn: { sm: '1 / -1' } }} />
              <TextField size="small" type="date" label="Starts" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} InputLabelProps={{ shrink: true }} helperText="Blank means immediately" />
              <TextField size="small" type="date" label="Ends" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} InputLabelProps={{ shrink: true }} error={!!errors.endDate} helperText={errors.endDate || 'Blank means no end'} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#64748b', mt: 2 }}>Who can see it</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControlLabel control={<Checkbox checked={form.visibility.includes('b2c')} onChange={() => visToggle('b2c')} />} label="B2C customers" />
              <FormControlLabel control={<Checkbox checked={form.visibility.includes('b2b')} onChange={() => visToggle('b2b')} />} label="B2B customers" />
            </Box>
            {errors.visibility && <Typography sx={{ color: '#d32f2f', fontSize: 12.5 }}>{errors.visibility}</Typography>}
          </Card>
          <Card title="Combo pictures" hint="Add up to 8. The first is the cover in lists; customers can flip through all of them on the website." accent="#ec4899">
            <OfferImages items={form.images} onChange={(images) => set('images', images)} error={errors.image} onError={(m) => setErrors((x) => ({ ...x, image: m || undefined }))} />
          </Card>
        </Box>
      )}

      {step === 1 && (
        <Card title="Products in the combo" hint="Pick the products and how many of each go in one combo" accent="#0ea5e9">
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2 }}>
            <TextField select size="small" label="Narrow by category" value={filterCat} onChange={(e) => { setFilterCat(e.target.value); setFilterSub(''); }}>
              <MenuItem value="">All categories</MenuItem>
              {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.title || c.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Narrow by subcategory" value={filterSub} disabled={!filterCat} onChange={(e) => setFilterSub(e.target.value)}>
              <MenuItem value="">All subcategories</MenuItem>
              {subcategories.map((s) => <MenuItem key={s._id} value={s._id}>{s.title || s.name}</MenuItem>)}
            </TextField>
          </Box>
          <Autocomplete multiple options={allProducts} loading={productsLoading} value={form.lines.map((l) => l.product)} disableCloseOnSelect filterSelectedOptions
            getOptionLabel={(o) => o.title || ''} isOptionEqualToValue={(o, v) => o._id === v._id}
            onChange={(_, v) => { setProducts(v); if (errors.products) setErrors((e) => ({ ...e, products: undefined })); }}
            renderTags={() => null}
            renderOption={(props, o) => (
              <li {...props} key={o._id}>
                <Avatar variant="rounded" src={imageUrl(o.product_images?.[0])} sx={{ width: 30, height: 30, mr: 1.25 }}><MdInventory2 size={16} /></Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}><Typography noWrap sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600 }}>{o.title}</Typography></Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', ml: 1, mr: 1, textAlign: 'right' }}>B2C {inr(o.b2c_sellingPrice || o.b2c_mrp)} · B2B {Number(o.b2b_sellingPrice) ? inr(o.b2b_sellingPrice) : '—'} · {o.availableQuantity ?? 0} in stock</Typography>
                <StockChip info={stockInfo(o, 1)} />
              </li>
            )}
            renderInput={(params) => <TextField {...params} size="small" label="Add products" placeholder="Search and select" error={!!errors.products} helperText={errors.products}
              InputProps={{ ...params.InputProps, endAdornment: <>{productsLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />} />
          <MixNotice products={form.lines.map((l) => l.product)} sx={{ mt: 2 }} />
          {form.lines.length > 0 && (
            <Box sx={{ mt: 2, border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
              {form.lines.map((l, i) => (
                <Box key={l.product._id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 90px 32px', md: '1fr 100px 110px 110px 32px' }, gap: 1.25, alignItems: 'center', p: 1.25, borderBottom: i < form.lines.length - 1 ? '1px solid #f1f5f9' : 'none', background: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Avatar variant="rounded" src={imageUrl(l.product.product_images?.[0])} sx={{ width: 40, height: 40 }}><MdInventory2 size={18} /></Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{l.product.title} <span style={{ fontSize: 10.5, fontWeight: 800, padding: '1px 6px', borderRadius: 8, background: isSeller(l.product) ? '#ede9fe' : '#e0f2fe', color: isSeller(l.product) ? '#5b21b6' : '#075985' }}>{isSeller(l.product) ? 'Seller' : 'Own'}</span></Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>MRP {inr(l.product.b2c_mrp)} · B2C {inr(l.product.b2c_sellingPrice || l.product.b2c_mrp)} · B2B {Number(l.product.b2b_sellingPrice) ? inr(l.product.b2b_sellingPrice) : '—'} · {l.product.availableQuantity ?? 0} in stock · {stockInfo(l.product, l.quantity).why}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}><StockChip info={stockInfo(l.product, l.quantity)} /></Box>
                  <TextField size="small" type="number" label="Qty" value={l.quantity} onChange={(e) => setQty(l.product._id, e.target.value)} inputProps={{ min: 1 }} />
                  <Typography sx={{ display: { xs: 'none', md: 'block' }, fontFamily: FONT, fontWeight: 800, fontSize: 13.5, textAlign: 'right' }}>{inr((Number(l.product.b2c_mrp) || 0) * l.quantity)}</Typography>
                  <IconButton size="small" aria-label="remove" onClick={() => setProducts(form.lines.filter((x) => x.product._id !== l.product._id).map((x) => x.product))}><MdClose size={16} /></IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Card>
      )}

      {step === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card title="Pricing" hint="The combo price replaces buying the products one by one" accent="#22c55e">
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, mb: 2 }}>
              {[['Total MRP', inr(mrp), '#6366f1'], ['Bought separately', inr(separately), '#0ea5e9'], ['Combos in stock', combosPossible, combosPossible > 0 ? '#16a34a' : '#dc2626']].map(([k, v, c]) => (
                <Box key={k} sx={{ p: 1.75, borderRadius: '12px', background: `${c}12`, border: `1px solid ${c}33` }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', fontWeight: 700 }}>{k}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800 }}>{v}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField size="small" type="number" label="Combo price — B2C *" value={form.b2c_sellingPrice} onChange={(e) => set('b2c_sellingPrice', e.target.value)} error={!!errors.b2c_sellingPrice}
                helperText={errors.b2c_sellingPrice || (savePct ? `Customers save ${savePct}% against MRP` : ' ')} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} inputProps={{ min: 0, step: '0.01' }} />
              <TextField size="small" type="number" label="Combo price — B2B" value={form.b2b_sellingPrice} onChange={(e) => set('b2b_sellingPrice', e.target.value)} error={!!errors.b2b_sellingPrice}
                helperText={errors.b2b_sellingPrice || 'Optional'} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} inputProps={{ min: 0, step: '0.01' }} />
            </Box>
            <Box sx={{ mt: 2 }}><PriceBreakdown lines={form.lines} b2cPrice={form.b2c_sellingPrice} b2bPrice={form.b2b_sellingPrice} /></Box>
            {mrp > 0 && b2cPrice > mrp && <Alert severity="warning" sx={{ mt: 2 }}>The B2C price is above the total MRP, so this is not a saving. Check the price.</Alert>}
            {separately > 0 && b2cPrice > separately && b2cPrice <= mrp && <Alert severity="info" sx={{ mt: 2 }}>The combo costs more than buying the products separately at their current selling prices.</Alert>}
          </Card>
          <Card title="Availability" accent="#f59e0b">
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />} label={form.isActive ? 'On — customers can buy it (within its dates)' : 'Off — hidden from customers'} />
          </Card>
        </Box>
      )}

      {step === 3 && (
        <Card title="Review" hint="Check everything, then save" accent="#f59e0b">
          <Box sx={{ p: 2, borderRadius: '12px', background: '#fffbeb', border: '1px solid #fde68a', mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Avatar variant="rounded" src={imageUrl(shownImage)} sx={{ width: 56, height: 56, bgcolor: '#fff' }}><MdInventory2 /></Avatar>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: '#78350f' }}>{form.title}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#a16207' }}>{form.comboCode.toUpperCase()} · {form.lines.length} product(s)</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, color: '#166534' }}>{inr(b2cPrice)}</Typography>
              {mrp > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8' }}><span style={{ textDecoration: 'line-through' }}>{inr(mrp)}</span>{savePct ? ` · ${savePct}% off` : ''}</Typography>}
            </Box>
          </Box>
          {[
            ['Runs', `${form.startDate || 'immediately'} → ${form.endDate || 'no end'}`],
            ['Customers', form.visibility.map((v) => v.toUpperCase()).join(' · ')],
            ['B2B price', form.b2b_sellingPrice === '' ? 'Not set' : inr(form.b2b_sellingPrice)],
            ['Status', form.isActive ? 'On' : 'Off'],
            ['Contents', form.lines.map((l) => `${l.quantity} × ${l.product.title}`).join(', ')],
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{v || '—'}</Typography>
            </Box>
          ))}
          {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
        </Card>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2.5 }}>
        <Button disabled={step === 0 || saving} onClick={() => setStep(step - 1)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', border: '1px solid #cbd5e1', borderRadius: '10px', px: 3 }}>Back</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} variant="contained" sx={yellowBtn}>Next</Button>
        ) : (
          <Button onClick={submit} variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create combo'}</Button>
        )}
      </Box>
    </Box>
  );
}

export default CreateComboOffer;
